import { useParams } from "react-router-dom";

import { useAbility } from "hooks/useAbility";
import { useActive } from "hooks/useActive";
import { useEnvironmentStatus } from "hooks/useEnvironmentStatus";
import { ACTION_APPROVE } from "models/role/action";
import { SUBJECT_REFUND } from "models/role/subject";
import { useSCA } from "modules/auth/hooks/useSCA";
import { VOP_STATUS } from "modules/common/constants/Vop";

import { useApproveRefund } from "../../../hooks/useApproveRefund";
import { useRefundDetails } from "../../../hooks/useRefundDetails";
import { getApproveButtonTooltipTranslationKey } from "../../../utils/getApproveButtonTooltipTranslationKey";

import type { ObjectWithId } from "utils/ObjectWithId";

export function useApproveButton() {
  const { approveRefund, isApproveRefundLoading } = useApproveRefund();
  const { id } = useParams<ObjectWithId>();
  const { refund } = useRefundDetails({ id });
  const { hasSCA, isLoading: isSCALoading } = useSCA();
  const { isSandbox, isProduction } = useEnvironmentStatus();
  const ability = useAbility();
  const { isActive, toggleActive } = useActive(false);

  const isVopPending = refund?.vop?.status === VOP_STATUS.PENDING;

  const canApproveRefund = ability.can(ACTION_APPROVE, SUBJECT_REFUND);

  const tooltipTitleTranslationKey = getApproveButtonTooltipTranslationKey(
    hasSCA,
    isSandbox,
    isVopPending
  );

  const isDisabled = Boolean(tooltipTitleTranslationKey);

  const handleButtonClick = async () => {
    if (isProduction) {
      toggleActive();
    } else {
      await approveRefund(id);
    }
  };

  const handleApproveRefund = async (scaToken: string) => {
    await approveRefund(id, scaToken);
  };

  const isLoading = isSCALoading || isApproveRefundLoading;

  return {
    id,
    handleButtonClick,
    isVisible: canApproveRefund,
    tooltipTitleTranslationKey,
    isDisabled,
    isLoading,
    isModalActive: isActive,
    toggleIsModalActive: toggleActive,
    handleApproveRefund,
  };
}
